import React, {Component} from 'react';
import PropTypes from 'prop-types';
import uuidV4 from 'uuid/v4';
import Checkbox from '@spothero/ui/checkbox';
import Form from '@spothero/ui/form';
import FormGroup from '@spothero/ui/form-group';
import Label from '@spothero/ui/label';
import Select from '@spothero/ui/select';
import TextButton from '@spothero/ui/text-button';
import TextInput from '@spothero/ui/text-input';
import EnvironmentUtils from '@spothero/utils/environment';
import UserUtils from 'user/user-utils';
import UserAPI from 'api/user';
import AlertModal from 'common/alert/AlertModal';
import UpdateUserCreditModal from 'user/credit/UpdateUserCreditModal';
import CustomerLookup from './CustomerLookup';

export default class HeroControls extends Component {
    static propTypes = {
        isAuthPending: PropTypes.bool,
        user: PropTypes.object.isRequired,
        rebookReservationId: PropTypes.number,
        isRebookingReservation: PropTypes.bool,
        purchaseForCustomer: PropTypes.bool,
        purchaseOnBehalfOfCustomer: PropTypes.bool,
        spotId: PropTypes.number,
        onHeroControlChange: PropTypes.func.isRequired,
        onHeroPurchasing: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        const {
            user,
            isRebookingReservation,
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer
        } = props;

        // save the admin user info since this component only shows if they are logged in as admin
        this._adminUser = user;
        this._customer = null;
        this._howDidYouHearItems = [
            {
                value: 'Unspecified',
                label: 'Unspecified'
            },
            {
                value: 'Craigslist',
                label: 'Craigslist'
            },
            {
                value: 'Referral from a friend',
                label: 'Referral from a friend'
            },
            {
                value: 'Search',
                label: 'Search'
            },
            {
                value: 'Other',
                label: 'Other'
            }
        ];

        this.state = {
            showAlertModal: false,
            showUpdateCreditModal: false,
            alertTitle: null,
            alertMessage: null,
            rebookReservationKey: uuidV4(),
            purchaseForCustomerKey: uuidV4(),
            purchaseOnBehalfOfCustomerKey: uuidV4(),
            prevIsRebookingReservation: isRebookingReservation,
            prevPurchaseForCustomer: purchaseForCustomer,
            prevPurchaseOnBehalfOfCustomer: purchaseOnBehalfOfCustomer
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            isRebookingReservation,
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer
        } = nextProps;
        const {
            prevIsRebookingReservation,
            prevPurchaseForCustomer,
            prevPurchaseOnBehalfOfCustomer
        } = prevState;
        const purchaseForChanged = (prevPurchaseForCustomer !== purchaseForCustomer);
        const purchaseOnBehalfChanged = (prevPurchaseOnBehalfOfCustomer !== purchaseOnBehalfOfCustomer);
        const isRebookingChanged = (prevIsRebookingReservation !== isRebookingReservation);

        if (purchaseForChanged || purchaseOnBehalfChanged || isRebookingChanged) {
            const newState = {
                prevIsRebookingReservation: isRebookingReservation,
                prevPurchaseForCustomer: purchaseForCustomer,
                prevPurchaseOnBehalfOfCustomer: purchaseOnBehalfOfCustomer
            };

            if (!purchaseForChanged) {
                newState.rebookReservationKey = uuidV4();
                newState.purchaseOnBehalfOfCustomerKey = uuidV4();
            }

            if (!purchaseOnBehalfChanged) {
                newState.rebookReservationKey = uuidV4();
                newState.purchaseForCustomerKey = uuidV4();
            }

            if (!isRebookingChanged) {
                newState.purchaseForCustomerKey = uuidV4();
                newState.purchaseOnBehalfOfCustomerKey = uuidV4();
            }

            return newState;
        }

        return null;
    }

    componentDidMount() {
        this._checkRebooking();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const {
            isRebookingReservation,
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer
        } = this.props;
        const {
            prevIsRebookingReservation,
            prevPurchaseForCustomer,
            prevPurchaseOnBehalfOfCustomer,
            showAlertModal
        } = prevState;

        if ((!purchaseForCustomer && !purchaseOnBehalfOfCustomer && !isRebookingReservation && !showAlertModal)
            && (prevIsRebookingReservation || prevPurchaseForCustomer || prevPurchaseOnBehalfOfCustomer)) {
            this._customer = null;

            this._showAlertModal({
                title: `And We're Back`,
                message: 'You are purchasing as your Hero self again! :)'
            });
        }
    }

    componentWillUnmount() {
        const {
            user,
            isAuthPending,
            isRebookingReservation,
            onHeroPurchasing,
        } = this.props;
        const data = {
            purchaseForCustomer: false,
            purchaseOnBehalfOfCustomer: false,
            paymentRequired: !isRebookingReservation
        };

        // if admin is not in the process of logging out
        if (!isAuthPending && user.status === UserUtils.AUTH_STATE.USER) {
            onHeroPurchasing({
                user: this._adminUser,
                reservation: data
            });
        }
    }

    _onAlertModalHidden = () => {
        this.setState({
            showAlertModal: false,
            alertTitle: null,
            alertMessage: null
        });
    }

    _onUpdateCreditDialogHidden = () => {
        this.setState({
            showUpdateCreditModal: false
        });
    }

    _onShowUpdateCreditModalClick = () => {
        this.setState({
            showUpdateCreditModal: true
        });
    }

    _onCreditUpdated = newCreditAmount => {
        const {
            onHeroControlChange,
            onHeroPurchasing,
            user
        } = this.props;
        const {
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer,
            isRebookingReservation
        } = this.props;

        this.setState({
            showUpdateCreditModal: false
        });

        user.spothero_credit = newCreditAmount; // eslint-disable-line camelcase

        this._customer = user;

        const data = {
            isRebookingReservation,
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer,
            paymentRequired: (isRebookingReservation) ? false : !purchaseForCustomer,
            creditApplied: (newCreditAmount > 0)
        };

        onHeroControlChange(data);

        onHeroPurchasing({
            user,
            reservation: data
        });
    }

    _onPurchaseForCustomerChange = () => {
        const {
            purchaseForCustomer,
            onHeroControlChange,
            onHeroPurchasing
        } = this.props;
        const data = {
            isRebookingReservation: false,
            purchaseForCustomer: !purchaseForCustomer,
            purchaseOnBehalfOfCustomer: false,
            paymentRequired: true
        };

        onHeroControlChange(data);

        if (purchaseForCustomer) {
            onHeroPurchasing({
                user: this._adminUser,
                reservation: data
            });
        }
    }

    _onPurchaseOnBehalfOfCustomerChange = () => {
        const {
            purchaseOnBehalfOfCustomer,
            onHeroControlChange,
            onHeroPurchasing
        } = this.props;
        const data = {
            isRebookingReservation: false,
            purchaseForCustomer: false,
            purchaseOnBehalfOfCustomer: !purchaseOnBehalfOfCustomer,
            paymentRequired: true
        };

        onHeroControlChange(data);

        if (purchaseOnBehalfOfCustomer) {
            onHeroPurchasing({
                user: this._adminUser,
                reservation: data
            });
        }
    }

    _onRebookingReservationChange = () => {
        const {
            isRebookingReservation,
            onHeroControlChange,
            onHeroPurchasing
        } = this.props;
        const data = {
            isRebookingReservation: !isRebookingReservation,
            purchaseForCustomer: false,
            purchaseOnBehalfOfCustomer: false,
            paymentRequired: true
        };

        onHeroControlChange(data);

        if (isRebookingReservation) {
            onHeroPurchasing({
                user: this._adminUser,
                reservation: data
            });
        } else {
            this._checkRebooking();
        }
    }

    _onCustomerLookupSubmit = customerLookupData => {
        const {
            onHeroPurchasing,
            onHeroControlChange,
            isRebookingReservation,
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer
        } = this.props;
        const {
            user,
            newUser,
            error
        } = customerLookupData;
        let alertTitle;
        let alertMessage;

        if (error) {
            this._showAlertModal({
                title: 'Whoops!',
                message: 'An error occurred while looking up the user. Please refresh the page and try again.'
            });

            return;
        }

        this._customer = user;

        const data = {
            isRebookingReservation,
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer,
            paymentRequired: (isRebookingReservation) ? false : !purchaseForCustomer
        };

        onHeroControlChange(data);

        onHeroPurchasing({
            user,
            reservation: data
        });

        if (newUser) {
            alertTitle = 'New User';
            alertMessage = `No user was found so a new account will be created during purchase. You are now purchasing as ${user.email}.`;

            console.log(`Please ignore previous 404: ${alertMessage}`); // eslint-disable-line no-console
        } else {
            alertTitle = 'Success!';
            alertMessage = `You are now purchasing as ${user.email}.`;
        }

        this._showAlertModal({
            title: alertTitle,
            message: alertMessage
        });
    }

    _showAlertModal({title, message}) {
        this.setState({
            showAlertModal: true,
            alertTitle: title,
            alertMessage: message
        });
    }

    async _checkRebooking() {
        const {
            rebookReservationId,
            onHeroControlChange,
            onHeroPurchasing
        } = this.props;

        if (rebookReservationId) {
            onHeroControlChange({
                isRebookingReservation: true,
                purchaseForCustomer: false,
                purchaseOnBehalfOfCustomer: false,
                rebookReservationKey: uuidV4()
            });

            try {
                const {
                    data: {
                        data
                    }
                } = await UserAPI.lookupByReservation(rebookReservationId);

                this._customer = data;

                const resData = {
                    purchaseForCustomer: false,
                    purchaseOnBehalfOfCustomer: false,
                    isRebookingReservation: true,
                    paymentRequired: false
                };

                onHeroControlChange(resData);

                onHeroPurchasing({
                    user: data,
                    reservation: resData
                });
            } catch (error) {
                this.setState({
                    showAlertModal: true,
                    alertTitle: 'Whoops!',
                    alertMessage: 'An error occurred while looking up the rebooking user. Please refresh the page and try again.'
                });
            }
        }
    }

    submit() {
        if (this.props.purchaseForCustomer) {
            return this._heroControlsForm.submit();
        } else {
            return true;
        }
    }

    render() {
        const {
            rebookReservationId,
            spotId,
            isRebookingReservation,
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer
        } = this.props;
        const {
            showAlertModal,
            alertTitle,
            alertMessage,
            showUpdateCreditModal,
            rebookReservationKey,
            purchaseForCustomerKey,
            purchaseOnBehalfOfCustomerKey
        } = this.state;

        return (
            <div className="HeroControls well well-sm admin-border">
                <h2>Hero Controls</h2>
                <a
                    className="HeroControls-facility-admin-page"
                    href={`/admin/spots/${spotId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Facility Admin Page
                </a>
                {rebookReservationId &&
                    <FormGroup>
                        <Checkbox
                            key={rebookReservationKey}
                            id="HeroControls-rebook-reservation"
                            name="isRebookingReservation"
                            label={<Label>Rebook Reservation ID: {rebookReservationId}</Label>}
                            defaultChecked={isRebookingReservation}
                            onChange={this._onRebookingReservationChange}
                        />
                    </FormGroup>
                }
                <FormGroup>
                    <Checkbox
                        key={purchaseForCustomerKey}
                        id="HeroControls-purchase-for-customer"
                        name="purchaseForCustomer"
                        label={<Label>Purchase for customer and charge SpotHero</Label>}
                        defaultChecked={purchaseForCustomer}
                        onChange={this._onPurchaseForCustomerChange}
                    />
                </FormGroup>
                <FormGroup>
                    <Checkbox
                        key={purchaseOnBehalfOfCustomerKey}
                        id="HeroControls-purchase-on-behalf-of-customer"
                        name="purchaseOnBehalfOfCustomer"
                        label={<Label>Purchase on behalf of customer using their account</Label>}
                        defaultChecked={purchaseOnBehalfOfCustomer}
                        onChange={this._onPurchaseOnBehalfOfCustomerChange}
                    />
                </FormGroup>
                {(purchaseForCustomer || purchaseOnBehalfOfCustomer) &&
                    <FormGroup>
                        <CustomerLookup
                            onSubmit={this._onCustomerLookupSubmit}
                        />
                    </FormGroup>
                }
                {purchaseForCustomer &&
                    <Form
                        ref={node => { this._heroControlsForm = node; }}
                        id="HeroControls-form"
                    >
                        <FormGroup>
                            <TextInput
                                name="purchaseForCustomerReferralSource"
                                label={<Label>Reason for purchasing</Label>}
                                placeholder={(EnvironmentUtils.isMobileDevice()) ? 'Reason for purchasing' : ''}
                            />
                        </FormGroup>
                        <FormGroup inline>
                            <Select
                                name="purchaseForCustomerDescription"
                                label={<Label>How did the customer hear of us?</Label>}
                                items={this._howDidYouHearItems}
                            />
                        </FormGroup>
                    </Form>
                }
                {(purchaseOnBehalfOfCustomer && this._customer && this._customer.id) &&
                    <TextButton
                        className="HeroControls-update-credit"
                        onClick={this._onShowUpdateCreditModalClick}
                    >
                        Update User Credit
                    </TextButton>
                }
                {showAlertModal &&
                    <AlertModal
                        title={alertTitle}
                        message={alertMessage}
                        onHidden={this._onAlertModalHidden}
                    />
                }
                {showUpdateCreditModal &&
                    <UpdateUserCreditModal
                        userId={this._customer.id}
                        credit={this._customer.spothero_credit / 100}
                        onHidden={this._onUpdateCreditDialogHidden}
                        onUpdated={this._onCreditUpdated}
                    />
                }
            </div>
        );
    }
}
